import { inject, Injectable } from '@angular/core';
import { DocumentRefService } from '@callrail/looky/util';
import { WhiteLabelAttributes } from './white-label-attributes';
import { WL_THEME_OVERRIDE_VARS } from './wl-brand-vars.generated';

// All CSS custom properties that customBrand() may set as inline styles.
// Cleared on every theme switch so design-tokens.css values take effect cleanly.
const WL_BRAND_VARS = [
  '--color-wl-primary',
  '--color-wl-secondary',
  '--color-wl-header-background',
  '--color-wl-header-foreground',
  '--color-wl-button-background',
  '--color-wl-button-foreground',
  '--color-action-primary-bg',
  '--color-action-primary-fg',
  '--color-action-primary-bg-hover',
  '--color-action-primary-opacity',
  '--color-action-secondary-bg-hover',
  '--color-action-secondary-border',
  '--color-action-secondary-fg',
  '--color-action-secondary-fg-hover',
  '--color-action-secondary-border-hover',
  '--color-action-ghost-primary-fg',
  '--color-action-ghost-primary-fg-hover',
  '--color-action-inline-fg',
  '--color-action-inline-fg-hover',
  '--color-border-focus',
  '--color-border-selected',
  '--color-border-emphasis',
  '--color-foreground-control-strong',
  '--color-foreground-selected',
  '--color-surface-control-selected',
  '--color-surface-control-selected-hover',
  '--color-surface-calendar-hover',
  '--color-status-progress-fg',
  '--color-navigation-global-item-border-active',
  '--color-navigation-side-item-fg',
  '--color-navigation-side-item-bg-hover',
  '--color-navigation-side-item-bg-active',
  '--color-navigation-top-item-border-hover',
  '--color-navigation-top-item-border-active',
  '--color-navigation-tab-border-active',
  '--color-domain-classification-key-terms-active-bg',
  '--color-domain-classification-agent',
] as const;

// TypeScript enforces that customBrand() returns exactly these keys — add to both or neither.
type BrandVars = { [K in (typeof WL_BRAND_VARS)[number]]: string };

// Legacy named-color vars that predate the semantic token system. They live in
// _color-base-theme.scss and fall back to CallRail blue (for CR) or gray (for WL).
// WL gray values are applied inline; clearing them on CR switch lets the CSS fallback win.
const LEGACY_BRAND_VARS: Record<string, string> = {
  '--color-attribluetion': 'var(--color-mine-shaft)',
  '--color-san-marino': 'var(--color-gun-powder)',
  '--color-dark-san-marino': 'var(--color-mine-shaft)',
  '--color-smugglers-cove': 'var(--color-mine-shaft)',
};

// Union of all vars that may differ between themes — cleared on every theme switch.
const WL_CLEAR_VARS = [
  ...new Set([
    ...WL_BRAND_VARS,
    ...WL_THEME_OVERRIDE_VARS,
    ...Object.keys(LEGACY_BRAND_VARS),
  ]),
];

@Injectable({
  providedIn: 'root',
})
export class CssVariablesService {
  private documentRefService = inject(DocumentRefService);

  // Tracks the luminance class added to <body> (e.g. 'white-label-dark')
  // so we can remove it on theme switch. Without this, the class persists
  // after CR → WL → CR cycles and leaks stale styles via .white-label-dark {}.
  private addedLuminanceClass: string | null = null;

  public buildWLTheme(brandColors: WhiteLabelAttributes = {}): void {
    // Activate [data-theme="white-label"] in design-tokens.css, which provides
    // gray fallback values for all semantic and action tokens.
    this.document.documentElement.setAttribute('data-theme', 'white-label');
    this.clearBrandVars();
    this.clearWlLuminanceClass();

    // Legacy brand-blue vars are always gray in WL regardless of brand color,
    // matching prod behavior where attribluetion/smugglers-cove map to mine-shaft.
    this.addVars(LEGACY_BRAND_VARS);

    if (brandColors.white_label_color) {
      this.addVars(this.customBrand(brandColors));
    }

    this.document.body.classList.add('white-label');
    if (brandColors.white_label_color_luminance_class) {
      this.addWlLuminanceClass(brandColors.white_label_color_luminance_class);
    }
  }

  public buildCallrailTheme(): void {
    // Removing data-theme restores the :root defaults in design-tokens.css,
    // which define all action tokens with CallRail blue values.
    // clearBrandVars() also removes legacy var inline overrides, letting the
    // CSS fallback in _color-base-theme.scss (blue) take effect.
    this.document.documentElement.removeAttribute('data-theme');
    this.clearBrandVars();
    this.clearWlLuminanceClass();
    this.document.body.classList.remove('white-label');
  }

  private get document() {
    return this.documentRefService.nativeDocument();
  }

  private clearBrandVars(): void {
    for (const prop of WL_CLEAR_VARS) {
      this.document.documentElement.style.removeProperty(prop);
    }
  }

  private addWlLuminanceClass(wlColor: string): void {
    this.document.body.classList.add(wlColor);
    this.addedLuminanceClass = wlColor;
  }

  private clearWlLuminanceClass(): void {
    if (this.addedLuminanceClass) {
      this.document.body.classList.remove(this.addedLuminanceClass);
      this.addedLuminanceClass = null;
    }
  }

  private customBrand(brandColors: WhiteLabelAttributes): BrandVars {
    const { white_label_color: color, white_label_icon_color: iconColor } =
      brandColors;
    const lum = brandColors.white_label_color_luminance ?? 0;

    // Nav background uses the brand color only for dark/mid-tone colors (luminance < 200).
    // Very light brand colors keep a white nav so text remains legible.
    const navBg = this.format(lum < 200 ? color! : 'var(--color-white)');
    const navFg = this.format(
      lum < 200 ? 'var(--color-white)' : iconColor ?? 'var(--color-mine-shaft)'
    );

    // Primary accent is the brand color used for interactive elements (buttons, borders,
    // focus rings, nav highlights, etc.). Very light colors (luminance >= 245) fall back
    // to mine-shaft to keep interactive elements visible against white backgrounds.
    const accent = this.format(lum < 245 ? color! : 'var(--color-mine-shaft)');
    const accentFg = this.format(
      lum < 245 ? iconColor ?? 'var(--color-white)' : 'var(--color-white)'
    );

    // Text-only hovers (tertiary, inline, icon) need a darker shade of the brand color.
    const accentHover = `color-mix(in srgb, ${accent} 80%, black)`;

    const primary = this.format(color!);
    const secondary = this.format(iconColor ?? '');

    return {
      '--color-wl-primary': primary,
      '--color-wl-secondary': secondary,

      '--color-wl-header-background': navBg,
      '--color-wl-header-foreground': navFg,

      '--color-wl-button-background': accent,
      '--color-wl-button-foreground': accentFg,

      // Hover uses the same brand color at 0.8 opacity rather than a darker shade,
      // matching the behavior in wl-misc.css.
      '--color-action-primary-bg': accent,
      '--color-action-primary-fg': accentFg,
      '--color-action-primary-bg-hover': accent,
      '--color-action-primary-opacity': '0.8',
      '--color-action-secondary-bg-hover': 'var(--color-mine-shaft)',
      '--color-action-secondary-border': 'var(--color-mine-shaft)',
      '--color-action-secondary-fg': 'var(--color-mine-shaft)',
      '--color-action-secondary-fg-hover': 'var(--color-white)',
      '--color-action-secondary-border-hover': 'var(--color-mine-shaft)',
      '--color-action-ghost-primary-fg': 'var(--color-mine-shaft)',
      '--color-action-ghost-primary-fg-hover': 'var(--color-mine-shaft)',
      '--color-action-inline-fg': accent,
      '--color-action-inline-fg-hover': accentHover,

      '--color-border-focus': accent,
      '--color-border-selected': accent,
      '--color-border-emphasis': accent,

      '--color-foreground-control-strong': accent,
      '--color-foreground-selected': 'var(--color-mine-shaft)',

      '--color-surface-control-selected': accent,
      '--color-surface-control-selected-hover': accent,
      '--color-surface-calendar-hover': accent,

      '--color-status-progress-fg': accent,

      '--color-navigation-global-item-border-active': accent,
      '--color-navigation-side-item-fg': accent,
      '--color-navigation-side-item-bg-hover': accent,
      '--color-navigation-side-item-bg-active': accent,
      '--color-navigation-top-item-border-hover': accent,
      '--color-navigation-top-item-border-active': accent,
      '--color-navigation-tab-border-active': accent,

      '--color-domain-classification-key-terms-active-bg': accent,
      '--color-domain-classification-agent': accent,
    };
  }

  private addVars(vars: Record<string, string>): void {
    for (const key of Object.keys(vars)) {
      this.document.documentElement.style.setProperty(key, vars[key]);
    }
  }

  private format(value: string): string {
    value = value.replace(/^#+/, '');
    return value.match(/^([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/)
      ? `#${value}`
      : value;
  }
}
